import { useState } from 'react';
import { X } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import {
    createReport,
    type Assessment,
    type AssessmentReview,
    type LevelKey,
    type Report,
} from '../../mentorshipService';

type Props = {
    assessment: Assessment;
    review: AssessmentReview | null;
    initialNotes: string;
    initialFinalScore: number;
    initialOverrideEnabled: boolean;
    onClose: () => void;
    onSubmitted: (report: Report) => void;
};

const LEVELS: LevelKey[] = ['Low', 'Mid', 'Senior'];

function ChipEditor({
    label,
    items,
    setItems,
    placeholder,
}: {
    label: string;
    items: string[];
    setItems: (next: string[]) => void;
    placeholder: string;
}) {
    const [draft, setDraft] = useState('');
    const add = () => {
        const t = draft.trim();
        if (!t) return;
        setItems([...items, t]);
        setDraft('');
    };
    return (
        <div className="report-submit__chip-editor">
            <label className="report-submit__label">{label}</label>
            <div className="report-submit__chips">
                {items.map((item, i) => (
                    <span key={i} className="mentorship-chip mentorship-chip--module">
                        {item}
                        <button
                            type="button"
                            className="report-submit__chip-remove"
                            aria-label={`Remove ${item}`}
                            onClick={() => setItems(items.filter((_, j) => j !== i))}
                        >
                            <X size={12} />
                        </button>
                    </span>
                ))}
            </div>
            <div className="report-submit__chip-input">
                <input
                    type="text"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            e.preventDefault();
                            add();
                        }
                    }}
                    placeholder={placeholder}
                />
                <Button variant="ghost" size="sm" onClick={add}>Add</Button>
            </div>
        </div>
    );
}

function ReportSubmitModal({
    assessment,
    review,
    initialNotes,
    initialFinalScore,
    initialOverrideEnabled,
    onClose,
    onSubmitted,
}: Props) {
    const ai = assessment.score_ai ?? 0;
    const [finalScore, setFinalScore] = useState<number>(initialFinalScore);
    const [agreedLevel, setAgreedLevel] = useState<LevelKey>(
        review?.level_mapping ?? 'Mid',
    );
    const [strengths, setStrengths] = useState<string[]>(
        assessment.justification?.strengths ?? [],
    );
    const [gaps, setGaps] = useState<string[]>(assessment.justification?.gaps ?? []);
    const [actions, setActions] = useState<string[]>([]);
    const [notes, setNotes] = useState(initialNotes);
    const [overrideReason, setOverrideReason] = useState('');
    const [benchmarkTag, setBenchmarkTag] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    const needsOverride = Math.abs(finalScore - ai) > 1;
    // Suppress unused-warning: initialOverrideEnabled is part of the reviewer→modal
    // contract — when override was enabled in the reviewer the user already chose
    // an out-of-range score, which `needsOverride` will detect from initialFinalScore.
    void initialOverrideEnabled;

    const canSubmit =
        actions.length > 0 &&
        notes.trim().length > 0 &&
        (!needsOverride || overrideReason.trim().length > 0) &&
        !isSaving;

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setIsSaving(true);
        setError(null);
        try {
            const report = await createReport({
                assessment_id: assessment.id,
                final_score: finalScore,
                agreed_level: agreedLevel,
                strengths,
                gaps,
                actions,
                mentor_notes: notes.trim(),
                override_reason: overrideReason.trim() || undefined,
                benchmark_tag: benchmarkTag.trim() || undefined,
            });
            onSubmitted(report);
        } catch (err) {
            console.error('[ReportSubmitModal] failed to submit', err);
            setError(err instanceof Error ? err.message : 'Submission failed');
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Modal open onClose={onClose} size="lg" ariaLabel="Finalize report">
            <Modal.Header onClose={onClose}>Finalize report</Modal.Header>
            <Modal.Body>
                <div className="report-submit__field">
                    <label className="report-submit__label">Final score: {finalScore}/5</label>
                    <input
                        type="range"
                        min={0}
                        max={5}
                        value={finalScore}
                        onChange={(e) => setFinalScore(Number(e.target.value))}
                    />
                    {needsOverride && (
                        <p className="report-submit__warn">
                            Outside AI ±1 — override reason required.
                        </p>
                    )}
                </div>

                <div className="report-submit__field">
                    <label className="report-submit__label">Agreed level</label>
                    <div className="report-submit__levels">
                        {LEVELS.map((lv) => (
                            <button
                                key={lv}
                                type="button"
                                className={`report-submit__level ${agreedLevel === lv ? 'is-active' : ''}`}
                                onClick={() => setAgreedLevel(lv)}
                            >
                                {lv}
                            </button>
                        ))}
                    </div>
                </div>

                <ChipEditor
                    label="Strengths"
                    items={strengths}
                    setItems={setStrengths}
                    placeholder="Add a strength…"
                />
                <ChipEditor
                    label="Gaps"
                    items={gaps}
                    setItems={setGaps}
                    placeholder="Add a gap…"
                />
                <ChipEditor
                    label="Development actions (required)"
                    items={actions}
                    setItems={setActions}
                    placeholder="Add a development action…"
                />

                <div className="report-submit__field">
                    <label className="report-submit__label" htmlFor="report-notes">
                        Mentor notes (required)
                    </label>
                    <textarea
                        id="report-notes"
                        rows={5}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Free-text feedback to the mentee…"
                    />
                </div>

                {needsOverride && (
                    <div className="report-submit__field">
                        <label className="report-submit__label" htmlFor="override-reason">
                            Override reason (required)
                        </label>
                        <textarea
                            id="override-reason"
                            rows={2}
                            value={overrideReason}
                            onChange={(e) => setOverrideReason(e.target.value)}
                            placeholder="Why are you scoring outside AI ±1?"
                        />
                    </div>
                )}

                <div className="report-submit__field">
                    <label className="report-submit__label" htmlFor="benchmark-tag">
                        Benchmark tag (optional)
                    </label>
                    <input
                        id="benchmark-tag"
                        type="text"
                        value={benchmarkTag}
                        onChange={(e) => setBenchmarkTag(e.target.value)}
                        placeholder="Defaults to <user>-<module>-<quarter>"
                    />
                </div>

                {error && <p className="report-submit__error">{error}</p>}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="ghost" size="md" onClick={onClose} disabled={isSaving}>
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    size="md"
                    onClick={handleSubmit}
                    disabled={!canSubmit}
                >
                    {isSaving ? 'Submitting…' : 'Finalize report'}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ReportSubmitModal;
